import { z } from 'zod'

import { recordPath, recordRoute } from '@/shared/registry'

import { classApi, classPath, classRoute } from '../class-form/class'

/** A class's attendance chart and sign-in sheets: beside Class Details, reached from its side menu. */
export const attendanceChartRoute = `${classRoute}/attendance`
export const signInSheetRoute = `${classRoute}/sign-in-sheet`

/** An organization's daily student check-in page, reached from Organization Details' side menu. */
export const studentCheckInRoute = recordRoute('classes', 'student-check-in')

export function attendanceChartPath(classId: string): string {
  return `${classPath(classId)}/attendance`
}

export function signInSheetPath(classId: string): string {
  return `${classPath(classId)}/sign-in-sheet`
}

export function studentCheckInPath(organizationId: string): string {
  return recordPath('classes', 'student-check-in', organizationId)
}

/** The API's attendance chart of a class. */
export function attendanceApi(classId: string): string {
  return `${classApi(classId)}/attendance`
}

/** Checks a student in to a session (PUT), or removes the check-in (DELETE). */
export function checkInApi(classId: string, sessionId: string, enrollmentId: string): string {
  return `${attendanceApi(classId)}/${encodeURIComponent(sessionId)}/${encodeURIComponent(enrollmentId)}`
}

/** The API's sign-in sheets of a class. */
export function signInSheetApi(classId: string): string {
  return `${classApi(classId)}/sign-in-sheet`
}

/** The API's daily check-in page of an organization, for `date` (`YYYY-MM-DD`) or today. */
export function studentCheckInApi(organizationId: string, date: string | null): string {
  const base = `/classes/v1/organizations/${encodeURIComponent(organizationId)}/check-ins`
  return date === null ? base : `${base}?${new URLSearchParams({ date }).toString()}`
}

/** The API's codes a sign-in sheet is refused with. */
export const signInSheetClosedCode = 'class_attendance.sign_in_sheet_closed'
export const noClassroomCode = 'class_attendance.no_classroom'

const sessionSchema = z.object({ sessionId: z.string(), startsOn: z.string(), endsOn: z.string() })

/** A session of the class. */
export type AttendanceSession = z.infer<typeof sessionSchema>

const names = { firstName: z.string(), lastName: z.string(), displayName: z.string() }

const chartSchema = z.object({
  classId: z.string(),
  className: z.string(),
  timeZone: z.string(),
  startsOn: z.string(),
  mayRecordAttendance: z.boolean(),
  sessions: z.array(sessionSchema),
  students: z.array(
    z.object({
      enrollmentId: z.string(),
      userProfileId: z.string(),
      ...names,
      checkIns: z.array(z.object({ sessionId: z.string(), checkedInOn: z.string() })),
    }),
  ),
})

/** A class's attendance chart. */
export type AttendanceChart = z.infer<typeof chartSchema>

export function parseAttendanceChart(body: unknown): AttendanceChart {
  return chartSchema.parse(body)
}

const checkInStudentSchema = z.object({
  enrollmentId: z.string(),
  userProfileId: z.string(),
  ...names,
  retake: z.boolean(),
  isNew: z.boolean(),
  checkedInOn: z.string().nullish(),
})

/** A student expected at a session on the check-in page. */
export type CheckInStudent = z.infer<typeof checkInStudentSchema>

const checkInDaySchema = z.object({
  organizationId: z.string(),
  organizationName: z.string(),
  date: z.string(),
  timeZone: z.string(),
  sessions: z.array(
    z.object({
      sessionId: z.string(),
      classId: z.string(),
      className: z.string(),
      startsOn: z.string(),
      endsOn: z.string(),
      primaryInstructorName: z.string().nullish(),
      offersSignInSheet: z.boolean(),
      rooms: z.array(z.object({ classroomId: z.string().nullish(), classroomName: z.string().nullish(), students: z.array(checkInStudentSchema) })),
    }),
  ),
})

/** An organization's daily check-in page. */
export type CheckInDay = z.infer<typeof checkInDaySchema>

/** One session on the check-in page. */
export type CheckInSession = CheckInDay['sessions'][number]

export function parseCheckInDay(body: unknown): CheckInDay {
  return checkInDaySchema.parse(body)
}

const signInSheetSchema = z.object({
  classId: z.string(),
  className: z.string(),
  timeZone: z.string(),
  sessions: z.array(sessionSchema),
  defaultSessionId: z.string(),
  classrooms: z.array(z.object({ classroomId: z.string(), name: z.string(), organizationName: z.string().nullish(), instructorNames: z.array(z.string()) })),
  students: z.array(
    z.object({
      enrollmentId: z.string(),
      userProfileId: z.string(),
      classroomId: z.string(),
      ...names,
      email: z.string().nullish(),
      organizationName: z.string().nullish(),
      retake: z.boolean(),
    }),
  ),
})

/** What a class's sign-in sheets print. */
export type SignInSheet = z.infer<typeof signInSheetSchema>

export function parseSignInSheet(body: unknown): SignInSheet {
  return signInSheetSchema.parse(body)
}
